import { useQuery } from '@tanstack/react-query';
import { BarChart3, ChevronRight, ClipboardList, FilePlus2, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { canAny } from '../../api/client';
import { fetchReportQuota } from '../../api/ops';
import { useSites } from '../../data/hooks';
import { usePresenceCounters } from '../../realtime/aggregates';
import { Card, Kpi, PageHeader } from '../../ui/components';
import { formatNumber } from '../../ui/format';
import { useMe } from '../auth/session';
import { EnergyChart } from './EnergyChart';
import { PresenceChart } from './PresenceChart';

const LINKS = [
  { key: 'indicators', to: '/reports/dashboard', icon: BarChart3 },
  { key: 'generate', to: '/reports/dashboard', icon: FilePlus2, perm: 'report.generate' },
  { key: 'stored', to: '/reports/reports', icon: ClipboardList },
  { key: 'points', to: '/reports/light-points', icon: Lightbulb },
] as const;

/**
 * Dashboard of a portal account (no operations.view: the authority observer, "AFM"). Simple: its client's light
 * points online / offline (live counters), the way to the AFM reports, the two charts. No alarms, no selectors.
 */
export function PortalDashboard() {
  const { t } = useTranslation();
  const me = useMe().data;
  const live = usePresenceCounters();
  const sites = useSites().data ?? [];
  const client = sites.find((s) => !sites.some((p) => p.id === s.parentId));
  const reports = canAny(me, 'report.view');
  const quota = useQuery({
    queryKey: ['reports', 'quota'],
    queryFn: fetchReportQuota,
    enabled: canAny(me, 'report.generate'),
  });
  const q = quota.data;
  return (
    <div className="page">
      <PageHeader title={client?.name ?? t('dashboard.title')} subtitle={t('portal.subtitle')} />
      <div className="grid grid-kpi">
        <Kpi label={t('portal.points')} value={formatNumber(live.online + live.offline + live.unknown)} />
        <Kpi label={t('dashboard.online')} value={formatNumber(live.online)} tone="online" />
        <Kpi label={t('dashboard.offline')} value={formatNumber(live.offline)} tone="offline" />
      </div>
      {reports && (
        <Card title={t('portal.reports.title')}>
          <p className="muted" style={{ marginTop: 0 }}>
            {t('portal.reports.intro')}
          </p>
          <nav className="portal-links" aria-label={t('portal.reports.title')}>
            {LINKS.filter((l) => !('perm' in l) || canAny(me, l.perm)).map((l) => (
              <Link key={l.key} to={l.to} className="portal-link">
                <l.icon size={22} strokeWidth={1.6} aria-hidden />
                <span>
                  <strong>{t(`portal.reports.${l.key}.title`)}</strong>
                  <span className="muted small">{t(`portal.reports.${l.key}.hint`)}</span>
                </span>
                <ChevronRight size={16} className="muted" aria-hidden />
              </Link>
            ))}
          </nav>
          {q?.limited && (
            <p className="muted small" style={{ marginBottom: 0 }}>
              {t('reportsPage.quota.counters', {
                today: q.used_today,
                daily: q.daily_limit,
                total: q.total,
                max: q.total_limit,
              })}
            </p>
          )}
        </Card>
      )}
      <PresenceChart />
      <EnergyChart />
    </div>
  );
}
